import React, { useState, Dispatch, SetStateAction, useEffect } from "react";
import { Modal, Row, Col, Dropdown, Button, Toast } from "react-bootstrap";
import { ActionHandlerFunction, AdminApprovalGroup, OrgInfo, UserProfile } from "@preveil-api";
import {
  OrgUserRoles, useAssignApprovalGroupActivityMutation, Account, AdminSuccessMessages, AdminErrorMessages, Message, useAppDispatch, MessageHandlerDisplayType, AdminApprovalGroupRole, AssignApprovalGroupActivityApiResponse,
  ApprovalRequestStatus, AdminWarningMessages, AdminUIActionTypes, MessageAnchors, MessageToastTypes
} from "src/common";
import { uiActions } from "src/store";
import _ from "lodash";

type AllProps = {
  setShow: Dispatch<SetStateAction<boolean>>;
  role: AdminApprovalGroupRole;
  approval_groups: AdminApprovalGroup[];
  handleAction: ActionHandlerFunction;
  existing_group?: AdminApprovalGroup;
  current_account: Account;
  org_info: OrgInfo;
};

// Description: Modal component for assigning an approval group to a specific role (either data export or admin management)
function AssignApprovalGroupModalComponent(props: AllProps) {
  const { setShow, role, approval_groups, handleAction, existing_group, current_account, org_info } = props;
  const dispatch = useAppDispatch();
  const ags = role.all_admin ? approval_groups.filter(a => isApprovalGroupAdminOnly(a)) : approval_groups;
  const [approval_group, setApprovalGroup] = useState<AdminApprovalGroup | undefined>(existing_group);

  const [assignApprovalGroup] = useAssignApprovalGroupActivityMutation();

  // Description: useEffect hook to set the approval group to the existing group if there is already one set
  useEffect(() => {
    setApprovalGroup(existing_group);
  }, [existing_group]);

  // Description: Checks if a specific approval group has all approvers with admin privileges
  function isApprovalGroupAdminOnly(approval_group: AdminApprovalGroup): boolean {
    return approval_group.approvers.filter((approver: UserProfile) => approver.role === OrgUserRoles.admin).length === approval_group.approvers.length;
  }

  // Description: Close the modal
  function close() {
    setShow(false);
    setApprovalGroup(existing_group);
  }

  // Description: Assign the approval group to the specific role using a crypto API call
  function handleAssign() {
    if (!!approval_group) {
      const api_params = {
        userId: current_account.user_id,
        orgId: org_info.org_id,
        approvalGroupUid: approval_group.approval_group_id,
        groupRole: role.role,
        groupVersion: approval_group.group_version,
      };
      assignApprovalGroup(api_params)
        .unwrap()
        .then((response: AssignApprovalGroupActivityApiResponse) => {
          if (!!response && response.request?.status === ApprovalRequestStatus.pending && !!existing_group) {
            const group_type = role.name;
            handleAction({ actionType: AdminUIActionTypes.Refresh });
            dispatch(uiActions.handleSetMessage(new Message(AdminWarningMessages.warning_pending_assign_group.replace(MessageAnchors.message_content, group_type).replace(MessageAnchors.prev_name, existing_group?.name), MessageHandlerDisplayType.warning, MessageToastTypes.warning)));
          } else {
            handleAction({ actionType: AdminUIActionTypes.Refresh });
            dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_assigned_approval_group)));
          }
        })
        .catch((msg) => {
          dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_assigning, MessageHandlerDisplayType.toastr), { msg }));
        });
      setShow(false);
    }
  }

  return (
    <Modal
      size="lg"
      show={true}
      onHide={() => setShow(false)}
      centered
      aria-label="Assign Approval Group"
    >
      <Modal.Header>
        <Modal.Title>Assign Approval Group</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!!role.all_admin && (
          <Toast bg="warning" show={true} className="d-inline-block mt-0 mb-3">
            <Toast.Body>All Approvers for this action must have admin privileges.</Toast.Body>
          </Toast>
        )}
        <p className="mb-3">
          Choose the group responsible for approving <b>{role.name}</b>
        </p>
        <Dropdown className="mb-3 pv-dropdown">
          <Dropdown.Toggle
            variant="outline-secondary"
            className="btn btn-sm"
            disabled={ags.length === 0}
          >
            {!!approval_group ? approval_group.name : "Select Approval Group"}
          </Dropdown.Toggle>
          <Dropdown.Menu flip={true} align="end">
            {_.map(ags, (approval_group: AdminApprovalGroup, i: number) => {
              return (
                <Dropdown.Item key={i} onClick={() => setApprovalGroup(approval_group)}>
                  {approval_group.name}
                </Dropdown.Item>
              );
            })}
          </Dropdown.Menu>
        </Dropdown>
        {!!approval_group && (
          <>
            <p>
              Members of <b>"{approval_group.name}"</b> Group
            </p>
            <div className="table-display">
              <Row className="header-card-row">
                <Col sm={5}>User</Col>
                <Col sm={6}>Role</Col>
              </Row>
              <div className="table-body">
                {_.map(approval_group.approvers, (approver: UserProfile, i: number) => {
                  return (
                    <Row key={i}>
                      <Col sm={5}>
                        {approver.name}
                      </Col>
                      <Col sm={4} className="with_cap">
                        {approver.role}
                      </Col>
                    </Row>
                  );
                })}
              </div>
            </div>
            <p>Approvers required for activity: {approval_group.required_approvers}</p>
          </>
        )}
        <div className="btn-panel">
          <Button disabled={!approval_group || (!!existing_group && approval_group.approval_group_id === existing_group?.approval_group_id)} onClick={handleAssign}>Assign</Button>
          <Button variant="no-outline-primary" onClick={close}>Close</Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default React.memo(AssignApprovalGroupModalComponent);
